class MixPack extends HTMLElement {
  connectedCallback() {
    this.count = parseInt(this.dataset.count || '2', 10);
    try { this.variants = JSON.parse(this.querySelector('.mixpack__data').textContent).variants || []; }
    catch (_e) { this.variants = []; }

    this.colorList = [];
    const seen = new Set();
    this.variants.forEach((v) => { if (v.color && !seen.has(v.color)) { seen.add(v.color); this.colorList.push({ name: v.color }); } });
    this.hasSize = this.variants.some((v) => v.size);
    this.slots = Array.from({ length: this.count }, () => ({ color: null, size: null }));

    this.toggle = this.querySelector('.mixpack__toggle');
    this.builder = this.querySelector('.mixpack__builder');
    this.slotsEl = this.querySelector('.mixpack__slots');
    this.addBtn = this.querySelector('.mixpack__add');
    this.addLabel = this.querySelector('.mixpack__add-label');

    // 2-pack builder only when not in enhance-only mode (e.g. carriers reuse just the buy-box polish)
    if (this.toggle && this.slotsEl && this.addBtn) {
      this.renderSlots();
      this.toggle.addEventListener('click', () => this.toggleOpen());
      this.addBtn.addEventListener('click', () => this.add());
    }
    this.enhanceNativePicker();
    setTimeout(() => this.enhanceBuyBox(), 150);
  }

  // Buy-box polish (preview-scoped): default size = Small, and move Shop Pay below the 2-pack.
  // NOTE: we intentionally do NOT block sold-out variants on the single buy box — the theme's
  // native "Notify Me" should show for OOS here. (OOS blocking lives in the 2-pack builder only.)
  enhanceBuyBox() {
    const fieldsets = Array.from(document.querySelectorAll('.option-selector'));
    const colourFS = fieldsets.find((fs) => fs.querySelector('.opt-label--swatch'));
    const sizeFS = fieldsets.find((fs) => fs !== colourFS && fs.querySelector('label[for]'));

    // default the selected size to Small (regardless of stock; if OOS the theme shows Notify Me)
    if (sizeFS) {
      const radios = Array.from(sizeFS.querySelectorAll('label[for]')).map((l) => document.getElementById(l.getAttribute('for'))).filter(Boolean);
      const t = radios.find((r) => /^(s|small)$/i.test((r.value || '').trim()));
      if (t && !t.checked) { t.checked = true; t.dispatchEvent(new Event('change', { bubbles: true })); }
    }

    // move Shop Pay installments below the 2-pack
    const pt = document.querySelector('shopify-payment-terms');
    if (pt) { const wrap = pt.closest('.shopify-payment-terms') || pt.parentElement || pt; this.after(wrap); }
  }

  colorAvailable(name) {
    if (this.hasSize) return this.sizesForColor(name).length > 0;
    return this.variants.some((v) => v.color === name && v.available);
  }

  // Buy box: inject a visible "Colour: Signature / Black" label above the swatches
  // (the theme's own colour legend is hidden when grouped), updating on hover/select
  // so the two blacks (Signature vs Cotton) are distinguishable.
  enhanceNativePicker() {
    const swatches = Array.from(document.querySelectorAll('.option-selector .opt-label--swatch'));
    if (!swatches.length) return;
    const fieldset = swatches[0].closest('.option-selector');
    if (!fieldset) return;
    let label = fieldset.querySelector('.mixpack-colour-label');
    if (!label) {
      label = document.createElement('div');
      label.className = 'mixpack-colour-label';
      const btns = fieldset.querySelector('.option-selector__btns');
      fieldset.insertBefore(label, btns);
    }
    const valueOf = (lbl) => { const id = lbl.getAttribute('for'); const r = id && document.getElementById(id); return r ? r.value : (lbl.dataset.swatch || ''); };
    const selected = () => { const c = fieldset.querySelector('input[type="radio"]:checked'); return c ? c.value : valueOf(swatches[0]); };
    const set = (v) => { label.textContent = 'Colour: ' + (v || '').replace('/', ' / '); };
    set(selected());
    swatches.forEach((lbl) => {
      const val = valueOf(lbl);
      lbl.addEventListener('mouseenter', () => set(val));
      lbl.addEventListener('mouseleave', () => set(selected()));
      lbl.addEventListener('click', () => setTimeout(() => set(selected()), 0));
    });
    this.groupBlacks(swatches, valueOf);
  }

  // Place the two black variants (e.g. Signature/Black + Cotton/Black) next to each other
  // so shoppers can compare, and tag each with its material. No-op unless a product has two
  // blacks; only runs where the mix-pack renders (i.e. the multi-pack products).
  groupBlacks(swatches, valueOf) {
    const lastToken = (v) => (v || '').split('/').pop().trim().toLowerCase();
    const materialOf = (v) => { const p = (v || '').split('/'); return p.length > 1 ? p[0].trim() : ''; };
    const blacks = swatches.filter((lbl) => lastToken(valueOf(lbl)) === 'black');
    if (blacks.length < 2) return;
    const first = blacks[0];
    for (let i = 1; i < blacks.length; i++) {
      if (first.nextSibling !== blacks[i]) first.parentNode.insertBefore(blacks[i], first.nextSibling);
    }
    blacks.forEach((lbl) => {
      const mat = materialOf(valueOf(lbl));
      if (mat) { lbl.setAttribute('title', 'Black · ' + mat); lbl.setAttribute('aria-label', 'Black · ' + mat); }
    });
  }

  setCval(i, value) {
    const el = this.slotsEl.querySelector(`.mixpack__cval[data-slot="${i}"]`);
    if (el) el.textContent = value ? ': ' + value.replace('/', ' / ') : '';
  }

  esc(s) { return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;'); }

  toggleOpen() {
    const open = this.toggle.getAttribute('aria-expanded') === 'true';
    this.toggle.setAttribute('aria-expanded', String(!open));
    this.builder.hidden = open;
  }

  sizesForColor(color) {
    return this.variants.filter((v) => v.color === color && v.size && v.available).map((v) => v.size);
  }

  renderSlots() {
    this.slotsEl.innerHTML = this.slots.map((_s, i) => this.slotHTML(i)).join('');
    this.slotsEl.querySelectorAll('.mixpack__swatch').forEach((b) => {
      b.addEventListener('click', () => this.pickColor(b));
      b.addEventListener('mouseenter', () => this.setCval(+b.dataset.slot, b.dataset.val));
      b.addEventListener('mouseleave', () => this.setCval(+b.dataset.slot, this.slots[+b.dataset.slot].color));
    });
    this.refresh();
  }

  slotHTML(i) {
    const swatches = this.colorList.map((c) => {
      const sw = (c.name.indexOf('/') >= 0 ? c.name.split('/').pop() : c.name).trim().toLowerCase();
      const oos = !this.colorAvailable(c.name);
      return `<button type="button" class="mixpack__swatch${oos ? ' mixpack__swatch--oos' : ''}" data-slot="${i}" data-val="${this.esc(c.name)}" data-swatch="${this.esc(sw)}" aria-pressed="false"${oos ? ' disabled aria-disabled="true"' : ''} title="${this.esc(c.name)}${oos ? ' (sold out)' : ''}" aria-label="${this.esc(c.name)}${oos ? ' — sold out' : ''}"></button>`;
    }).join('');
    const sizeArea = this.hasSize
      ? `<div class="mixpack__optlabel">Size</div><div class="mixpack__sizes" data-slot="${i}"><span class="mixpack__hint">Choose a colour first</span></div>`
      : '';
    return `<div class="mixpack__slot"><div class="mixpack__slot-h">Item ${i + 1}</div>` +
      `<div class="mixpack__optlabel">Colour <span class="mixpack__cval" data-slot="${i}"></span></div><div class="mixpack__swatches">${swatches}</div>${sizeArea}</div>`;
  }

  pickColor(btn) {
    const i = +btn.dataset.slot;
    this.slots[i].color = btn.dataset.val;
    this.slots[i].size = null;
    this.slotsEl.querySelectorAll(`.mixpack__swatch[data-slot="${i}"]`).forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
    this.setCval(i, btn.dataset.val);
    this.renderSizes(i);
    this.syncGallery(btn.dataset.val);
    this.refresh();
  }

  // Picking a colour swaps the main product image by selecting that colour in the
  // theme's native variant picker, which fires the theme's own gallery switch.
  syncGallery(colorValue) {
    try {
      const radios = document.querySelectorAll('.option-selector .js-option, .option-selector input[type="radio"]');
      for (const r of radios) {
        if (r.value === colorValue && !r.checked) {
          r.checked = true;
          r.dispatchEvent(new Event('input', { bubbles: true }));
          r.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    } catch (_e) { /* preview-only nicety; ignore if structure differs */ }
  }

  renderSizes(i) {
    if (!this.hasSize) return;
    const cont = this.slotsEl.querySelector(`.mixpack__sizes[data-slot="${i}"]`);
    const sizes = this.sizesForColor(this.slots[i].color);
    cont.innerHTML = sizes.length
      ? sizes.map((s) => `<button type="button" class="mixpack__size-btn" data-slot="${i}" data-val="${this.esc(s)}" aria-pressed="false">${this.esc(s)}</button>`).join('')
      : '<span class="mixpack__soldout">This colour is sold out</span>';
    cont.querySelectorAll('.mixpack__size-btn').forEach((b) => b.addEventListener('click', () => this.pickSize(b)));
  }

  pickSize(btn) {
    const i = +btn.dataset.slot;
    this.slots[i].size = btn.dataset.val;
    this.slotsEl.querySelectorAll(`.mixpack__size-btn[data-slot="${i}"]`).forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
    this.refresh();
  }

  variantFor(slot) { return this.variants.find((v) => v.color === slot.color && (this.hasSize ? v.size === slot.size : true)); }

  refresh() {
    const colorsDone = this.slots.every((s) => s.color);
    const ready = this.slots.every((s) => {
      if (!s.color || (this.hasSize && !s.size)) return false;
      const v = this.variantFor(s);
      return v && v.available;
    });
    this.addBtn.toggleAttribute('disabled', !ready);
    this.addBtn.setAttribute('aria-disabled', String(!ready));
    if (ready) this.addLabel.textContent = `Add ${this.count}-Pack to Bag`;
    else if (this.hasSize && colorsDone) this.addLabel.textContent = 'Select your sizes';
    else this.addLabel.textContent = `Select ${this.count} colours`;
  }

  async add() {
    const items = this.slots.map((s) => ({ id: this.variantFor(s).id, quantity: 1 }));
    this.addBtn.setAttribute('disabled', '');
    this.addLabel.textContent = 'Adding…';
    try {
      const drawer = document.querySelector('cart-drawer') || document.querySelector('.js-cart-drawer');
      const header = document.querySelector('page-header');
      const updatable = [drawer, header].filter((el) => el && el.dataset && el.dataset.sectionId);
      const fd = new FormData();
      items.forEach((it, i) => { fd.append(`items[${i}][id]`, it.id); fd.append(`items[${i}][quantity]`, it.quantity); });
      if (updatable.length) { fd.append('sections', updatable.map((el) => el.dataset.sectionId).join(',')); fd.append('sections_url', window.location.pathname); }
      const route = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add';
      const res = await fetch(route, { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'add failed');
      if (data.sections) updatable.forEach((el) => { if (el.updateFromCartChange) el.updateFromCartChange(data.sections[el.dataset.sectionId]); });
      const d = document.querySelector('.js-cart-drawer');
      if (d && d.open) d.open();
      this.toggleOpen();
    } catch (e) { console.error('mix-pack add failed', e); }
    this.refresh();
  }
}

if (!customElements.get('mix-pack')) customElements.define('mix-pack', MixPack);
